import React, { useState } from 'react';
import { AIReadinessAssessment, ContactForm, CompleteAIRoadmap } from './components';
import { Brain, Target, Sparkles } from 'lucide-react';
import type { AppState, AssessmentResults, ContactInfo } from './types';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<AppState>('landing');
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResults | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  const handleAssessmentComplete = (results: AssessmentResults) => {
    setAssessmentResults(results);
    setCurrentView('contact');
  };

  const handleContactComplete = (contact: ContactInfo) => {
    setContactInfo(contact);
    setCurrentView('roadmap');
  };

  const handleBackToAssessment = () => {
    setCurrentView('assessment');
  };

  const handleBackToContact = () => {
    setCurrentView('contact');
  };

  const handleStartAssessment = () => {
    setCurrentView('assessment');
  };

  const renderLanding = () => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-beige/10 to-gray-100">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-brand-red/10 text-brand-red px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            Based on the Amazon Best-Selling "Amplify AI" Book Framework
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-brand-black mb-6">
            Transform Your Business with
            <span className="bg-gradient-to-r from-brand-red to-brand-gray bg-clip-text text-transparent"> AI</span>
          </h1>
          
          <p className="text-xl text-brand-gray mb-8 max-w-3xl mx-auto">
            Discover exactly how AI can revolutionize your business using the proven SymbioEthical Framework 
            from Ronsley Vaz's acclaimed book "Amplify AI: Integrating Intelligence, Preserving Humanity".
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={handleStartAssessment}
              className="bg-brand-red text-brand-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-red-700 transition-all transform hover:scale-105 flex items-center shadow-lg"
            >
              <Brain className="w-6 h-6 mr-3" />
              Start Free Assessment
            </button>
            <div className="text-sm text-brand-gray">
              8 minutes • $2,500 value • Expert implementation support available
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-brand-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-brand-beige/20">
            <div className="bg-brand-red/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-brand-red" />
            </div>
            <h3 className="text-xl font-bold text-brand-black mb-3">Business Vital Signs</h3>
            <p className="text-brand-gray">
              Comprehensive analysis of your current business health across revenue, brand, audience, and operations.
            </p>
          </div>
          
          <div className="bg-brand-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-brand-beige/20">
            <div className="bg-brand-gray/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-brand-gray" />
            </div>
            <h3 className="text-xl font-bold text-brand-black mb-3">AI Readiness Score</h3>
            <p className="text-brand-gray">
              Evaluate your organization's current AI implementation level and investment capacity for optimal results.
            </p>
          </div>
          
          <div className="bg-brand-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-brand-beige/20">
            <div className="bg-brand-beige/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-brand-red" />
            </div>
            <h3 className="text-xl font-bold text-brand-black mb-3">Custom Roadmap</h3>
            <p className="text-brand-gray">
              Get a personalized 90-day implementation plan with specific tools, strategies, and ROI projections.
            </p>
          </div>
        </div>

        {/* Book Credibility & Social Proof */}
        <div className="bg-brand-white p-8 rounded-xl shadow-lg border border-brand-beige/20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-brand-black mb-4">Based on the Amazon Best-Selling Book</h3>
              <div className="mb-4">
                <h4 className="text-xl font-semibold text-brand-red mb-2">"Amplify AI: Integrating Intelligence, Preserving Humanity"</h4>
                <p className="text-brand-gray mb-3">
                  A comprehensive guide for business acceleration using ethical AI implementation.
                </p>
                <div className="bg-brand-beige/10 p-4 rounded-lg mb-4">
                  <p className="text-sm text-brand-gray italic">
                    "If you buy one book on AI, this is the one. Ronsley Seriojo Vaz is a true practitioner and has written 
                    the ultimate book to understand the potential of AI and how to use its tremendous power in a human way."
                  </p>
                  <p className="text-xs text-brand-gray mt-2">— Amazon Customer Review</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <a 
                  href="https://amplifyais.com/book" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brand-red text-brand-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  Get the Book
                </a>
                <div className="text-sm text-brand-gray">
                  Available on Amazon, Barnes & Noble, Apple Books
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <h4 className="text-lg font-bold text-brand-black mb-4">About the Author</h4>
              <div className="bg-brand-beige/10 p-6 rounded-lg">
                <h5 className="font-bold text-brand-black mb-2">Ronsley Seriojo Vaz</h5>
                <div className="text-sm text-brand-gray space-y-1">
                  <p>• Computer Scientist & TEDx Speaker</p>
                  <p>• 5.1M+ Podcast Downloads (133 Countries)</p>
                  <p>• Founder of AmplifyAI Solutions</p>
                  <p>• 1,500+ Business Leader Interviews</p>
                  <p>• Masters in Software Engineering & MBA</p>
                </div>
                <div className="mt-4 pt-4 border-t border-brand-beige">
                  <p className="font-semibold text-brand-black mb-2">Framework Results:</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <span><span className="text-brand-red font-bold">10X ROI</span> in 90 days</span>
                    <span><span className="text-brand-red font-bold">40+ hours</span> saved weekly</span>
                    <span><span className="text-brand-red font-bold">30%</span> sales increase</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAssessment = () => (
    <AIReadinessAssessment onComplete={handleAssessmentComplete} />
  );

  const renderContact = () => (
    assessmentResults ? (
      <ContactForm 
        assessmentResults={assessmentResults}
        onComplete={handleContactComplete}
        onBack={handleBackToAssessment}
      />
    ) : (
      <div>Error: No assessment results</div>
    )
  );

  const renderRoadmap = () => (
    <CompleteAIRoadmap 
      assessmentResults={assessmentResults}
      contactInfo={contactInfo}
      onBackToAssessment={handleBackToContact}
    />
  );

  return (
    <div className="App">
      {currentView === 'landing' && renderLanding()}
      {currentView === 'assessment' && renderAssessment()}
      {currentView === 'contact' && renderContact()}
      {currentView === 'roadmap' && renderRoadmap()}
    </div>
  );
};

export default App;